import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import PaymentService from '../../../services/PaymentService'
import { useSettings } from '../../../hooks/useSettings'
import EmbeddedCheckoutPanel from './Payment/EmbeddedCheckoutPanel'

/**
 * Formatta un prezzo ricorrente come arriva dal server.
 *
 * @param {{amountCents:number, currency:string, interval:string}|null} price
 * @param {string} lingua
 * @returns {string|null} null quando il prezzo non esiste — e allora si mostra
 *   il contatto commerciale, non uno zero: «gratis» sarebbe una promessa.
 */
export const formatPrice = (price, lingua = 'en') => {
  if (!price || typeof price.amountCents !== 'number') return null
  const importo = new Intl.NumberFormat(lingua, {
    style: 'currency',
    currency: (price.currency || 'eur').toUpperCase(),
  }).format(price.amountCents / 100)
  return importo
}

/**
 * Cosa scrivere accanto a un modulo. Il prezzo, quando c'e', si mostra sempre:
 * e' un listino (ticket 6a9e5f9c), anche per i moduli che da qui non si
 * comprano — il sistema di pagamento e' ancora da scegliere, e l'attivazione
 * passa da MedLea. Senza prezzo resta la distinzione fra "chiedi
 * all'amministratore" (il modulo lo concede solo lui) e "contatta MedLea".
 *
 * @param {{active:boolean, adminOnly:boolean}} modulo
 * @param {string|null} prezzo - gia' formattato, null se non esiste
 * @returns {[string, object|undefined]} argomenti per `t(...)`
 */
export const moduleAction = (modulo, prezzo) => {
  if (modulo.active) return ['subscription.included']
  // zero = modulo gratuito dichiarato (es. chatbot), non «0,00 € / mese»
  if (modulo.price && modulo.price.amountCents === 0)
    return ['subscription.free']
  if (prezzo) return ['subscription.pricePerMonth', { price: prezzo }]
  if (modulo.adminOnly) return ['subscription.askAdmin']
  return ['subscription.contactForPrice']
}

/**
 * Il prezzo di un piano. Zero e' un piano gratuito dichiarato (non «0,00 € /
 * mese»); nessun prezzo vuol dire su preventivo.
 *
 * @param {{amountCents:number, currency:string}|null} price
 * @param {string} lingua
 * @returns {[string, object|undefined]} argomenti per `t(...)`
 */
export const planPriceLabel = (price, lingua = 'en') => {
  if (price && price.amountCents === 0) return ['subscription.free']
  const prezzo = formatPrice(price, lingua)
  if (prezzo) return ['subscription.pricePerMonth', { price: prezzo }]
  return ['subscription.contactForPrice']
}

/**
 * Quale controllo mostrare su un modulo: attivare, disattivare, o nessuno.
 *
 * Solo i moduli `purchasable` si comprano in autonomia. Un backend piu' vecchio non manda
 * il campo: senza `purchasable` non si mostra nulla (meglio nessun pulsante che uno che
 * porta a una porta chiusa).
 *
 * @param {{active:boolean, purchasable?:boolean}} modulo
 * @returns {'activate'|'deactivate'|null}
 */
export const moduleControl = (modulo) => {
  if (!modulo.purchasable) return null
  // Gia' disdetto a fine periodo (pulsante o link.com): resta acceso fino ad
  // allora, un'altra disattivazione non cambierebbe niente.
  if (modulo.active && modulo.endsAt) return null
  return modulo.active ? 'deactivate' : 'activate'
}

/**
 * Quale controllo mostrare sulla card di un piano (spec digiscan-web 2026-10-26 §8).
 *
 * Il server dice se l'account puo' comprare da se' (`planSelfService`): con
 * `allowed: false` (interruttore spento, macchina dedicata, piano gestito da MedLea)
 * nessun acquisto ne' cambio, resta la nota «contatta MedLea». La disdetta del piano
 * attuale invece vale sempre per chi ha un abbonamento Stripe. Un backend piu' vecchio
 * non manda questi campi: nessun pulsante.
 *
 * @param {{current:boolean, purchasable?:boolean}} piano
 * @param {{planSelfService?:{allowed:boolean}, planBilling?:string|null, planEndsAt?:number|null}} stato
 * @returns {'subscribe'|'change'|'cancel'|null}
 */
export const planControl = (piano, stato) => {
  const stripePlan = stato?.planBilling === 'stripe'
  if (piano.current) return stripePlan && !stato?.planEndsAt ? 'cancel' : null
  if (!piano.purchasable || !stato?.planSelfService?.allowed) return null
  // Un piano gia' disdetto finisce alla sua data: dopo si potra' sceglierne un altro.
  if (stato?.planEndsAt) return null
  return stripePlan ? 'change' : 'subscribe'
}

/**
 * La data di fine di un modulo disdetto (epoch in secondi dal server).
 *
 * @param {number} endsAt
 * @param {string} lingua
 * @returns {string}
 */
export const formatEndDate = (endsAt, lingua = 'en') =>
  new Intl.DateTimeFormat(lingua, { dateStyle: 'medium' }).format(
    new Date(endsAt * 1000)
  )

const POLL_EVERY_MS = 2000
const POLL_MAX_TRIES = 15

// Indirizzo commerciale per i clienti (confermato da Simone, 24/09): va accanto
// a ogni «contatta MedLea». Sta qui e non nelle traduzioni perche' questo file
// e' condiviso con LeaRes, i file di lingua no.
export const CONTACT_EMAIL = 'accounts@medlea-tech.com'

const ContactEmail = () => (
  <a
    data-testid="subscription-contact-email"
    href={`mailto:${CONTACT_EMAIL}`}
    className="ml-1 not-italic text-blue-600 underline"
  >
    {CONTACT_EMAIL}
  </a>
)

/**
 * Il pannello «Subscription» della stazione di ricarica.
 *
 * Mostra il piano attivo con le sue quote reali, i moduli accesi e quelli
 * spenti. Il listino e' parziale: dove il server risponde `price: null` qui si
 * mostra «contatta MedLea», mai uno zero. I moduli `purchasable` si attivano e
 * disattivano da qui (Stripe Checkout al primo acquisto, poi item sulla
 * subscription); gli altri li concede un amministratore o MedLea.
 */
const Subscription = () => {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const { t, i18n } = useTranslation('payments')
  const { isDarkMode } = useSettings()

  const [pending, setPending] = useState({})
  const [actionError, setActionError] = useState(false)
  // Checkout incorporata aperta (Managed Payments): {moduleId, clientSecret, publishableKey}.
  const [embedded, setEmbedded] = useState(null)
  // Moduli disdetti in questa sessione: id -> epoch di fine (Managed Payments).
  const [scheduled, setScheduled] = useState({})
  const timers = useRef({})
  // Letto una sola volta al mount: l'effect sotto ripulisce l'URL, cosi' un rimontaggio
  // (cambio tab, riapertura modale) trova la query gia' consumata e non riparte.
  const [returnedModule] = useState(() => {
    const q = new URLSearchParams(window.location.search)
    return q.get('module_checkout') === 'success'
      ? q.get('module') || null
      : null
  })
  // Ritorno dalla Checkout incorporata: Stripe aggiunge la sessione.
  const [returnedSession] = useState(() =>
    new URLSearchParams(window.location.search).get('session_id')
  )
  // Ritorno dall'acquisto di un piano (…/account?plan_checkout=success&plan=<id>).
  const [returnedPlan] = useState(() => {
    const q = new URLSearchParams(window.location.search)
    return q.get('plan_checkout') === 'success' ? q.get('plan') || null : null
  })
  // Piano in attesa di conferma da Stripe, o azione sul piano in corso: id del piano.
  const [planPending, setPlanPending] = useState(null)
  // Upgrade il cui pagamento non e' (ancora) riuscito: Stripe non ha cambiato il piano.
  const [planPaymentPending, setPlanPaymentPending] = useState(false)
  const [planError, setPlanError] = useState(false)

  const load = useCallback(() => {
    setIsLoading(true)
    setHasError(false)
    PaymentService.getSubscription()
      .then((r) => setData(r?.data || null))
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const clearPending = useCallback((id) => {
    clearInterval(timers.current[id])
    delete timers.current[id]
    setPending((p) => {
      const { [id]: _done, ...rest } = p
      return rest
    })
  }, [])

  // L'attivazione/disattivazione vera arriva dal webhook Stripe, non dalla risposta alla
  // POST: si rilegge l'abbonamento finche' il modulo non e' nello stato atteso.
  const waitForModule = useCallback(
    (id, expectedActive) => {
      let tries = 0
      clearInterval(timers.current[id])
      timers.current[id] = setInterval(async () => {
        tries += 1
        try {
          const r = await PaymentService.getSubscription()
          const found = (r?.data?.modules || []).find((m) => m.id === id)
          if (found && found.active === expectedActive) {
            setData(r.data)
            clearPending(id)
            return
          }
        } catch {
          /* riprova al giro dopo */
        }
        if (tries >= POLL_MAX_TRIES) {
          clearPending(id)
          setActionError(true)
          load()
        }
      }, POLL_EVERY_MS)
    },
    [clearPending, load]
  )

  const changeModule = async (id, activate) => {
    setActionError(false)
    setPending((p) => ({
      ...p,
      [id]: activate ? 'activating' : 'deactivating',
    }))
    try {
      const r = activate
        ? await PaymentService.checkoutModule(id)
        : await PaymentService.deactivateModule(id)
      if (r?.data?.status === 'redirect' && r.data.url) {
        window.location.assign(r.data.url)
        return
      }
      // Managed Payments: si paga qui dentro; l'attesa riparte al ritorno da Stripe.
      if (r?.data?.status === 'embedded' && r.data.clientSecret) {
        setEmbedded({
          title: t(`subscription.modules.${id}`, { defaultValue: id }),
          clientSecret: r.data.clientSecret,
          publishableKey: r.data.publishableKey,
        })
        clearPending(id)
        return
      }
      // Disdetto a fine periodo: il modulo resta acceso, niente da aspettare.
      if (r?.data?.status === 'scheduled') {
        setScheduled((p) => ({ ...p, [id]: r.data.endsAt }))
        clearPending(id)
        return
      }
      waitForModule(id, activate)
    } catch {
      clearPending(id)
      setActionError(true)
      // Una UI stantia (409 «gia' attivo», 404) va riallineata a cio' che dice il server.
      load()
    }
  }

  // Il piano lo cambia Stripe (webhook o conferma della sessione): si rilegge
  // l'abbonamento finche' il tier non e' quello atteso.
  const waitForPlan = useCallback(
    (tier) => {
      let tries = 0
      setPlanPending(tier)
      clearInterval(timers.current.__plan)
      timers.current.__plan = setInterval(async () => {
        tries += 1
        try {
          const r = await PaymentService.getSubscription()
          if (r?.data?.tier === tier) {
            clearInterval(timers.current.__plan)
            setData(r.data)
            setPlanPending(null)
            return
          }
        } catch {
          /* riprova al giro dopo */
        }
        if (tries >= POLL_MAX_TRIES) {
          clearInterval(timers.current.__plan)
          setPlanPending(null)
          setPlanError(true)
          load()
        }
      }, POLL_EVERY_MS)
    },
    [load]
  )

  const changePlan = async (piano, control) => {
    setPlanError(false)
    setPlanPaymentPending(false)
    if (
      control === 'cancel' &&
      !window.confirm(t('subscription.planCancelConfirm'))
    )
      return
    setPlanPending(piano.id)
    try {
      if (control === 'subscribe') {
        const r = await PaymentService.checkoutPlan(piano.id)
        if (r?.data?.status === 'embedded' && r.data.clientSecret) {
          setEmbedded({
            title: t(`subscription.tiers.${piano.id}`, {
              defaultValue: piano.id,
            }),
            clientSecret: r.data.clientSecret,
            publishableKey: r.data.publishableKey,
          })
        }
      } else if (control === 'change') {
        const r = await PaymentService.changePlan(piano.id)
        if (r?.data?.status === 'pending') setPlanPaymentPending(true)
        load()
      } else {
        await PaymentService.cancelPlan()
        load()
      }
    } catch {
      setPlanError(true)
      load()
    } finally {
      setPlanPending(null)
    }
  }

  // Ritorno da Stripe Checkout (…/account?module_checkout=success&module=<id>): la query
  // si consuma subito, l'attesa parte solo per `success` con un modulo.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    if (q.has('module_checkout') || q.has('plan_checkout')) {
      q.delete('module_checkout')
      q.delete('module')
      q.delete('plan_checkout')
      q.delete('plan')
      q.delete('session_id')
      const rest = q.toString()
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname +
          (rest ? `?${rest}` : '') +
          window.location.hash
      )
    }
    if (returnedModule) {
      setPending((p) => ({ ...p, [returnedModule]: 'activating' }))
      if (returnedSession) {
        // Checkout incorporata: il server rilegge la sessione da Stripe e allinea
        // i moduli subito, senza aspettare il webhook; l'attesa parte comunque,
        // qualunque sia l'esito.
        PaymentService.confirmCheckout(returnedSession)
          .catch(() => null)
          .then(() => waitForModule(returnedModule, true))
      } else {
        waitForModule(returnedModule, true)
      }
    }
    if (returnedPlan) {
      // Stessa strada dei moduli: il server rilegge la sessione e allinea il piano subito.
      const confirm = returnedSession
        ? PaymentService.confirmCheckout(returnedSession).catch(() => null)
        : Promise.resolve()
      confirm.then(() => waitForPlan(returnedPlan))
    }
    const current = timers.current
    return () => Object.values(current).forEach(clearInterval)
  }, [
    waitForModule,
    waitForPlan,
    returnedModule,
    returnedPlan,
    returnedSession,
  ])

  if (isLoading) {
    return (
      <div
        data-testid="subscription-loading"
        role="status"
        className="flex items-center justify-center p-8"
      >
        <span
          aria-hidden="true"
          className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"
        />
      </div>
    )
  }

  if (hasError || !data) {
    return (
      <p
        data-testid="subscription-error"
        className={clsx('p-4 text-sm', {
          'text-gray-700': !isDarkMode,
          'text-gray-300': isDarkMode,
        })}
      >
        {t('subscription.loadError')}
      </p>
    )
  }

  if (embedded) {
    return (
      <EmbeddedCheckoutPanel
        publishableKey={embedded.publishableKey}
        clientSecret={embedded.clientSecret}
        title={embedded.title}
        onBack={() => {
          setEmbedded(null)
          load()
        }}
      />
    )
  }

  const quote = data.quotas || {}
  const quoteText = (q) =>
    t('subscription.quotas', {
      jobs: q.maxConcurrentJobs ?? '—',
      storage: q.maxStorageGb ?? '—',
    })

  return (
    <div className="flex flex-col gap-6">
      {/* Piano attivo */}
      <section data-testid="subscription-plan">
        <h3
          className={clsx('text-sm font-semibold mb-2', {
            'text-gray-800': !isDarkMode,
            'text-gray-100': isDarkMode,
          })}
        >
          {t('subscription.planTitle')}
        </h3>
        <div
          className={clsx('rounded-lg border p-4', {
            'border-gray-300 bg-white': !isDarkMode,
            'border-gray-700 bg-gray-800': isDarkMode,
          })}
        >
          <p
            className={clsx('text-lg font-bold', {
              'text-gray-900': !isDarkMode,
              'text-white': isDarkMode,
            })}
          >
            {t(`subscription.tiers.${data.tier}`, { defaultValue: data.tier })}
          </p>
          <p
            className={clsx('mt-1 text-xs', {
              'text-gray-600': !isDarkMode,
              'text-gray-400': isDarkMode,
            })}
          >
            {quoteText(quote)}
          </p>
          <p
            data-testid="subscription-plan-price"
            className={clsx('mt-2 text-sm', {
              'text-gray-800': !isDarkMode,
              'text-gray-200': isDarkMode,
            })}
          >
            {t(...planPriceLabel(data.price, i18n.language))}
          </p>
        </div>
      </section>

      {/* Listino dei piani: un backend piu' vecchio non manda `plans` */}
      {Array.isArray(data.plans) && data.plans.length > 0 && (
        <section data-testid="subscription-plans">
          <h3
            className={clsx('text-sm font-semibold mb-2', {
              'text-gray-800': !isDarkMode,
              'text-gray-100': isDarkMode,
            })}
          >
            {t('subscription.plansTitle')}
          </h3>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {data.plans.map((piano) => (
              <li
                key={piano.id}
                data-testid={`subscription-plan-${piano.id}`}
                className={clsx('rounded-lg border p-3', {
                  'border-blue-600': piano.current,
                  'border-gray-300 bg-white': !isDarkMode && !piano.current,
                  'border-gray-700 bg-gray-800': isDarkMode && !piano.current,
                  'bg-white': !isDarkMode && piano.current,
                  'bg-gray-800': isDarkMode && piano.current,
                })}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <p
                    className={clsx('text-sm font-bold', {
                      'text-gray-900': !isDarkMode,
                      'text-white': isDarkMode,
                    })}
                  >
                    {t(`subscription.tiers.${piano.id}`, {
                      defaultValue: piano.id,
                    })}
                  </p>
                  {piano.current && (
                    <span className="text-xs font-medium text-blue-600">
                      {t('subscription.currentPlan')}
                    </span>
                  )}
                </div>
                <p
                  className={clsx('mt-1 text-sm', {
                    'text-gray-800': !isDarkMode,
                    'text-gray-200': isDarkMode,
                  })}
                >
                  {t(...planPriceLabel(piano.price, i18n.language))}
                </p>
                <p
                  className={clsx('mt-1 text-xs', {
                    'text-gray-600': !isDarkMode,
                    'text-gray-400': isDarkMode,
                  })}
                >
                  {quoteText(piano.quotas || {})}
                </p>
                {piano.current && data.planEndsAt && (
                  <p
                    data-testid={`subscription-plan-${piano.id}-ends`}
                    className="mt-1 text-xs text-gray-500"
                  >
                    {t('subscription.activeUntil', {
                      date: formatEndDate(data.planEndsAt, i18n.language),
                    })}
                  </p>
                )}
                {piano.current && data.planNext && data.planNext.at && (
                  <p
                    data-testid={`subscription-plan-${piano.id}-next`}
                    className="mt-1 text-xs text-gray-500"
                  >
                    {t('subscription.planNextOn', {
                      plan: t(`subscription.tiers.${data.planNext.tier}`, {
                        defaultValue: data.planNext.tier,
                      }),
                      date: formatEndDate(data.planNext.at, i18n.language),
                    })}
                  </p>
                )}
                {(() => {
                  const control = planControl(piano, data)
                  if (!control) return null
                  return (
                    <button
                      type="button"
                      data-testid={`subscription-plan-${piano.id}-${control}`}
                      disabled={Boolean(planPending)}
                      onClick={() => changePlan(piano, control)}
                      className="mt-2 rounded border border-blue-600 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                    >
                      {planPending === piano.id
                        ? t('subscription.planWorking')
                        : t(`subscription.plan_${control}`)}
                    </button>
                  )
                })()}
              </li>
            ))}
          </ul>
          {planError && (
            <p
              data-testid="subscription-plan-error"
              className="mt-2 text-xs text-red-600"
            >
              {t('subscription.actionError')}
            </p>
          )}
          {planPaymentPending && (
            <p
              data-testid="subscription-plan-payment-pending"
              className="mt-2 text-xs text-amber-600"
            >
              {t('subscription.planPaymentPending')}
            </p>
          )}
          {data.planSelfService?.reason === 'managed_by_medlea' && (
            <p
              data-testid="subscription-plan-managed-note"
              className={clsx('mt-2 text-xs', {
                'text-gray-600': !isDarkMode,
                'text-gray-400': isDarkMode,
              })}
            >
              {t('subscription.planManagedNote')}
              <ContactEmail />
            </p>
          )}
          <p
            data-testid="subscription-dedicated-note"
            className={clsx('mt-2 text-xs', {
              'text-gray-600': !isDarkMode,
              'text-gray-400': isDarkMode,
            })}
          >
            {t('subscription.dedicatedNote')}
            <ContactEmail />
          </p>
        </section>
      )}

      {/* Moduli */}
      <section data-testid="subscription-modules">
        <h3
          className={clsx('text-sm font-semibold mb-2', {
            'text-gray-800': !isDarkMode,
            'text-gray-100': isDarkMode,
          })}
        >
          {t('subscription.modulesTitle')}
        </h3>
        <ul className="flex flex-col gap-2">
          {(data.modules || []).map((modulo) => {
            const prezzo = formatPrice(modulo.price, i18n.language)
            const endsAt = scheduled[modulo.id] || modulo.endsAt
            const control = moduleControl({ ...modulo, endsAt })
            return (
              <li
                key={modulo.id}
                data-testid={`subscription-module-${modulo.id}`}
                className={clsx(
                  'flex items-center justify-between gap-3 rounded-lg border p-3',
                  {
                    'border-gray-300 bg-white': !isDarkMode,
                    'border-gray-700 bg-gray-800': isDarkMode,
                  }
                )}
              >
                <p
                  className={clsx('min-w-0 truncate text-sm font-medium', {
                    'text-gray-900': !isDarkMode,
                    'text-white': isDarkMode,
                  })}
                >
                  {t(`subscription.modules.${modulo.id}`, {
                    defaultValue: modulo.id,
                  })}
                </p>
                {/* Due colonne a destra, larghezza fissa perche' restino
                    allineate fra le righe: prezzo, poi la spunta se attivo. */}
                <div className="flex flex-shrink-0 items-center gap-3">
                  <span
                    data-testid={`subscription-module-${modulo.id}-price`}
                    className={clsx('w-40 text-right text-xs', {
                      'text-gray-700': !isDarkMode,
                      'text-gray-300': isDarkMode,
                    })}
                  >
                    {t(...moduleAction(modulo, prezzo))}
                  </span>
                  <span
                    data-testid={`subscription-module-${modulo.id}-status`}
                    role="img"
                    aria-label={
                      modulo.active
                        ? t('subscription.moduleActive')
                        : t('subscription.moduleInactive')
                    }
                    className="w-5 text-center text-base font-bold text-green-500"
                  >
                    {modulo.active ? '✓' : ''}
                  </span>
                  {modulo.active && endsAt && (
                    <span
                      data-testid={`subscription-module-${modulo.id}-ends`}
                      className="text-xs text-gray-500"
                    >
                      {t('subscription.activeUntil', {
                        date: formatEndDate(endsAt, i18n.language),
                      })}
                    </span>
                  )}
                  {control && (
                    <button
                      type="button"
                      data-testid={`subscription-module-${modulo.id}-${control}`}
                      disabled={Boolean(pending[modulo.id])}
                      onClick={() =>
                        changeModule(modulo.id, control === 'activate')
                      }
                      className="rounded border border-blue-600 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                    >
                      {pending[modulo.id]
                        ? t(`subscription.${pending[modulo.id]}`)
                        : t(`subscription.${control}`)}
                    </button>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
        {actionError && (
          <p
            data-testid="subscription-action-error"
            className="mt-3 text-xs text-red-600"
          >
            {t('subscription.actionError')}
          </p>
        )}
        <p
          className={clsx('mt-3 text-xs italic', {
            'text-gray-600': !isDarkMode,
            'text-gray-400': isDarkMode,
          })}
        >
          {t('subscription.pricingNote')}
          <ContactEmail />
        </p>
      </section>
    </div>
  )
}

export default Subscription
